"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOfferDrafting = useOfferDrafting;
const react_1 = __importDefault(require("react"));
const react_router_dom_1 = require("react-router-dom");
const use_offer_api_1 = require("@src/components/pages/admin/modules/offer-create-or-edit/use-offer-api");
const offer_model_1 = require("@src/components/pages/admin/modules/offer-create-or-edit/offer-model");
const lodash_1 = require("lodash");
function useOfferDrafting({ isDraftingEnabled, isContextInitialized, draftSlug, offerPaymentType, offerPaymentTypeInitialValues, offerBasicInfo, offerBasicInfoInitialValues, offerAssets, offerAssetsInitialValues, }) {
    const history = (0, react_router_dom_1.useHistory)();
    const [isSavingDraft, setIsSavingDraft] = react_1.default.useState(false);
    const shouldSaveDraftOnUnmountRef = react_1.default.useRef(isDraftingEnabled);
    const { saveOfferDraft } = (0, use_offer_api_1.useOfferApi)();
    // Using these refs for performance reasons
    // When using offer values directly from offer context, each change to offer causes function "handleSaveDraftClick" to change referential value
    // This results in rerender everytime offer changes in components where "handleSaveDraftClick" is retrieved from context
    // Using ref and manually synchronizing it through useEffect enables us to avoid this problem by preserving referential equality with useCallback
    const offerPaymentTypeValuesRef = react_1.default.useRef({});
    const offerBasicInfoValuesRef = react_1.default.useRef({});
    const offerAssetsValuesRef = react_1.default.useRef({});
    const offerPaymentTypeInitialValuesRef = react_1.default.useRef({});
    const offerBasicInfoInitialValuesRef = react_1.default.useRef({});
    const offerAssetsInitialValuesRef = react_1.default.useRef({});
    react_1.default.useEffect(() => {
        offerPaymentTypeValuesRef.current = offerPaymentType;
    }, [offerPaymentType]);
    react_1.default.useEffect(() => {
        offerBasicInfoValuesRef.current = offerBasicInfo;
    }, [offerBasicInfo]);
    react_1.default.useEffect(() => {
        offerAssetsValuesRef.current = offerAssets;
    }, [offerAssets]);
    react_1.default.useEffect(() => {
        offerBasicInfoInitialValuesRef.current = offerBasicInfoInitialValues;
    }, [offerBasicInfoInitialValues]);
    react_1.default.useEffect(() => {
        offerPaymentTypeInitialValuesRef.current = offerPaymentTypeInitialValues;
    }, [offerPaymentTypeInitialValues]);
    react_1.default.useEffect(() => {
        offerAssetsInitialValuesRef.current = offerAssetsInitialValues;
    }, [offerAssetsInitialValues]);
    const saveOfferDraftIfValuesAreChanged = react_1.default.useCallback(async () => {
        if ((0, offer_model_1.areOfferValuesChanged)({
            // billingProfileId is breaking the comparison for a draft vs initial values
            offerPaymentTypeValues: (0, lodash_1.omit)(offerPaymentTypeValuesRef.current, 'billingProfileId'),
            offerPaymentTypeValuesInitialValues: offerPaymentTypeInitialValuesRef.current,
            offerBasicInfoValues: offerBasicInfoValuesRef.current,
            offerBasicInfoInitialValues: offerBasicInfoInitialValuesRef.current,
            offerAssetsValues: offerAssetsValuesRef.current,
            offerAssetsInitialValues: offerAssetsInitialValuesRef.current,
            isEditingExistingDraft: Boolean(draftSlug),
        })) {
            await saveOfferDraft({
                draftSlug,
                offerPaymentType: offerPaymentTypeValuesRef.current,
                offerBasicInfo: offerBasicInfoValuesRef.current,
                offerAssets: offerAssetsValuesRef.current,
            });
        }
    }, [draftSlug]);
    const handleSaveDraftClick = react_1.default.useCallback(async () => {
        if (!isDraftingEnabled) {
            return;
        }
        setIsSavingDraft(true);
        shouldSaveDraftOnUnmountRef.current = false;
        await saveOfferDraftIfValuesAreChanged();
        history.pushLocalized('/admin');
    }, [saveOfferDraftIfValuesAreChanged]);
    react_1.default.useEffect(() => {
        return () => {
            if (shouldSaveDraftOnUnmountRef.current && isDraftingEnabled && isContextInitialized) {
                saveOfferDraftIfValuesAreChanged();
            }
        };
    }, [saveOfferDraftIfValuesAreChanged, isContextInitialized, isDraftingEnabled]);
    react_1.default.useEffect(() => {
        if (!isDraftingEnabled || !isContextInitialized) {
            return;
        }
        window.addEventListener('beforeunload', saveOfferDraftIfValuesAreChanged);
        return () => {
            window.removeEventListener('beforeunload', saveOfferDraftIfValuesAreChanged);
        };
    }, [saveOfferDraftIfValuesAreChanged, isDraftingEnabled, isContextInitialized]);
    return { handleSaveDraftClick, isSavingDraft, shouldSaveDraftOnUnmountRef };
}
